import { FC } from "react";
import { DeleteV2Icon, HelpIcon } from "../../../../assets/img/icons";
import { CreateTrade } from "../../../../types/Trade";

import { useMediaQuery } from "@mui/material";
import IconButton from "../../../../components/IconButton";
import Input from "../../../../components/Input";
import Text from "../../../../components/Text";
import TextArea from "../../../../components/TextArea";
import { FieldErrors } from "../../../../utils/errors";
import { IMAGE_FILE_ACCEPT } from "../../../../utils/fileAccept";
import { Select } from "../../../OldSelect";
import Tooltip from "../../../Tooltip";
import styles from "./styles.module.scss";

interface Props {
    trades: CreateTrade[];
    setTrades: (trades: CreateTrade[]) => void;
    onAddFile?: (tradeId: string, file: File) => void;
    onRemoveFile?: (tradeId: string) => void;
    mode: "add" | "edit";
    fieldErrors?: FieldErrors;
    onClearFieldError?: (key: string) => void;
}

const TradeList: FC<Props> = ({
    trades,
    setTrades,
    onAddFile,
    onRemoveFile,
    mode,
    fieldErrors = {},
    onClearFieldError,
}) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");

    const handleRemoveTrade = (tradeId: string) => {
        setTrades(trades.filter((trade) => trade.id !== tradeId));
        onRemoveFile?.(tradeId);
    };

    const handleTradeChange = (index: number, updatedTrade: CreateTrade) => {
        const updatedTrades = [...trades];
        updatedTrades[index] = updatedTrade;

        setTrades(updatedTrades);
    };

    const handleFieldTradeChange = (
        index: number,
        field: keyof CreateTrade,
        value: any
    ) => {
        const updatedTrade = { ...trades[index], [field]: value };
        handleTradeChange(index, updatedTrade);
        const tradeId = trades[index].id;
        if (field === "result") {
            onClearFieldError?.(`trade.${tradeId}.result`);
        }
        if (field === "coherentPlan") {
            onClearFieldError?.(`trade.${tradeId}.coherentPlan`);
        }
    };

    const handleResultChange = (index: number, value: string) => {
        const normalizedValue = value.replace(/\./g, ",");
        if (/^-?\d*(,\d*)?$/.test(normalizedValue)) {
            handleFieldTradeChange(index, "result", normalizedValue);
        }
    };

    return (
        <div className={styles.tradesList}>
            {/* ajouts de Tony - Clé unique avec trade.id au lieu de idx */}
            {trades.map((trade, idx) => (
                <div key={trade.id} className={styles.tradeContainer}>
                    <div className={styles.row}>
                        <div className={styles.tradeResult}>
                            <Text variant="body-bold">Gain / Perte</Text>
                            <Input
                                type="text"
                                inputMode="decimal"
                                value={trade.result ?? ""}
                                onChanged={(value) =>
                                    handleResultChange(idx, value)
                                }
                                error={fieldErrors[`trade.${trade.id}.result`]}
                            />
                        </div>
                        <div className={styles.tradeRespect}>
                            <Text variant="body-bold">Respect méthode</Text>
                            <Select
                                value={
                                    trade.coherentPlan === null ||
                                    trade.coherentPlan === undefined
                                        ? ""
                                        : String(trade.coherentPlan)
                                }
                                onChanged={(value) =>
                                    handleFieldTradeChange(
                                        idx,
                                        "coherentPlan",
                                        value === "true"
                                            ? true
                                            : value === "false"
                                            ? false
                                            : null
                                    )
                                }
                                error={
                                    fieldErrors[
                                        `trade.${trade.id}.coherentPlan`
                                    ]
                                }
                            >
                                <option value="">-</option>
                                <option value="true">Oui</option>
                                <option value="false">Non</option>
                            </Select>
                        </div>
                        {isMobile && mode === "add" && (
                            <div
                                className={`${styles.tradeOptions} ${styles.deleteTradeButtonWrap}`}
                            >
                                <IconButton
                                    icon={DeleteV2Icon}
                                    size="medium"
                                    color="secondary"
                                    onClick={() => handleRemoveTrade(trade.id)}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.tradeImage}>
                        <Tooltip
                            placement="bottom"
                            content="Télécharger une image de votre plan d'entrée pour ce trade."
                        >
                            <div className={styles.tradeImageLabel}>
                                <Text variant="body-bold">Plan d'entrée</Text>
                                <HelpIcon className={styles.helpIcon} />
                            </div>
                        </Tooltip>
                        {/* ajouts de Tony - ID unique pour chaque trade */}
                        <Input
                            type="file"
                            accept={IMAGE_FILE_ACCEPT}
                            fileInputId={`input_trade_img_${trade.id}`}
                            value={trade.fileId ? "Changer d'image" : ""}
                            onChanged={(files) => {
                                const file = files[0] || null;
                                if (!file) return;
                                handleFieldTradeChange(idx, "fileId", trade.id);
                                onAddFile?.(trade.id, file);
                            }}
                        />
                    </div>
                    <div className={styles.tradeComment}>
                        <Text
                            variant="body-bold"
                            className={styles.tradeCommentLabel}
                        >
                            Commentaire
                        </Text>
                        <TextArea
                            className={styles.tradeCommentField}
                            value={trade.comment ?? ""}
                            onChange={(e) =>
                                handleFieldTradeChange(
                                    idx,
                                    "comment",
                                    e.target.value
                                )
                            }
                            maxLength={2500}
                        />
                    </div>
                    {!isMobile && mode === "add" && (
                        <div
                            className={`${styles.tradeOptions} ${styles.deleteTradeButtonWrap}`}
                        >
                            <IconButton
                                icon={DeleteV2Icon}
                                size="medium"
                                color="secondary"
                                onClick={() => handleRemoveTrade(trade.id)}
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default TradeList;
